import React from 'react';
import { Info, Layout, Type, FileText, DollarSign, Target, Square } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const ResumeAdvantageChecklist = () => {
  const ChecklistSection = ({ icon: Icon, title, items }) => (
    <div className="bg-blue-50 p-6 rounded-lg mb-6">
      <div className="flex items-start gap-4">
        <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="w-full">
          <h3 className="font-semibold mb-4">{title}</h3>
          <ul className="space-y-4">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <h1 className="text-[32px] font-bold mb-2">Ultimate Answer Formula&reg;: Turn Interviews Into Offers</h1>
        <p className="text-gray-600 text-lg mb-12">Master these proven techniques to transform your interview responses into job offers.</p>

        {/* Introduction */}
        <div className="mb-12">
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-gray-800">
              The difference between a good interview and a great one often lies not in what you say, but how you say it. 
              These battle-tested techniques, refined through extensive 1:1 coaching sessions, will help you stand out 
              from other candidates and significantly increase your success rate in interviews.
            </p>
          </div>
        </div>

        {/* Section 1: The Power of Concise Communication */}
        <div className="mb-12">
          <h2 className="text-[24px] font-semibold mb-6">1. Less is More: The Art of Concise Communication 🎨</h2>
          
          <div className="space-y-6">
            <div className="pl-4 border-l-2 border-gray-200">
              <p className="text-gray-800 mb-4">
                One of the most common mistakes candidates make is providing excessive detail in their responses. 
                Master the art of being succinct while still delivering impact.
              </p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg mb-6">
              <p className="font-medium mb-2">💡 Key Formula:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Challenge faced</li>
                <li>Action taken</li>
                <li>Result achieved</li>
                <li>Follow-up offer: "Did I provide the answer you're looking for? I'm happy to elaborate if you'd like."</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium mb-2">⚠️ Why This Matters:</p>
              <ul className="list-disc  pl-6 space-y-2">
                <li>Interviewers often need to check specific boxes</li>
                <li>Long-winded answers can diminish your perceived value</li>
                <li>Maintains engagement and interest</li>
                <li>Shows respect for the interviewer's time</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 2: The "I Can Do This" Strategy */}
        <div className="mb-12">
          <h2 className="text-[24px] font-semibold mb-6">2. The Power of "I Can Do This For You" 💪</h2>

          <div className="space-y-6">
            <div className="pl-4 border-l-2 border-gray-200">
              <p className="text-gray-800 mb-4">
                Understanding the psychology of hiring managers and interview panels is crucial. Your success depends 
                on effectively communicating your ability to solve their specific problems.
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-medium mb-2">🎯 Key Insights:</p>
              <ul className="list-disc pl-6  space-y-3">
                <li>Only the hiring manager truly gains from your hire</li>
                <li>Other interviewers may be only 60% mentally present</li>
                <li>Make it easy for them to advocate for you</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium mb-2">✨ Winning Phrases:</p>
              <div className="space-y-3 pl-4">
                <p className="italic">"I can do this for you..."</p>
                <p className="italic">"I have done that exact process many times throughout my career and I can do that for you and [Company Name]..."</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: The Conversation Strategy */}
        <div className="mb-12">
          <h2 className="text-[24px] font-semibold mb-6">3. Transform Interrogations into Conversations 🤝</h2>

          <div className="space-y-6">
            <div className="pl-4 border-l-2 border-gray-200">
              <p className="text-gray-800 mb-4">
                The key to interview success lies in creating genuine dialogue rather than enduring an interrogation. 
                Your goal is to engage the interviewer in meaningful conversation.
              </p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <p className="font-medium mb-2">🎯 The Golden Rule:</p>
              <p>The person who talks the most during any conversation always thinks it went fantastic.</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="font-medium mb-2">💡 Strategic Approach:</p>
              <ul className="list-disc pl-6  space-y-3">
                <li>Aim for 50/50 talking ratio</li>
                <li>Ask engaging follow-up questions</li>
                <li>Listen actively and show genuine interest</li>
                <li>Build upon the interviewer's responses</li>
              </ul>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-medium mb-2">📈 Results:</p>
              <ul className="list-disc pl-6  space-y-2">
                <li>Places you in the top 2-3% of candidates</li>
                <li>Creates memorable interactions</li>
                <li>Builds authentic connections</li>
                <li>Significantly increases success rate</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-[20px] font-semibold mb-4">Your Path to Success 🚀</h2>
          <p className="text-gray-800 mb-4">
            By implementing these strategies consistently, you'll transform your interview performance from standard 
            responses to compelling conversations that lead to job offers. Remember, success in interviews isn't just 
            about having the right qualifications—it's about communicating them effectively.
          </p>
          <p className="text-gray-800">
            Practice these techniques before your interviews, remain mindful of your delivery, and focus on engaging 
            in meaningful dialogue rather than simply answering questions. Your ability to master these elements will 
            set you apart from other candidates and significantly increase your chances of receiving offers.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ResumeAdvantageChecklist; 